import { api } from './client'

/** Os dois frameworks que o CHECK da tabela aceita (MIGRATION_346). */
export type Framework = 'pci_dss' | 'iso_27001'

/** Como um ambiente se relaciona com o certificado — e só três formas.
 *
 *  `transicao` existe porque `interno` mentiria: descreve apoio permanente, e o que importa
 *  num ambiente em virada é o prazo. Decisão do proprietário em 28/09/2026 sobre o
 *  TECPAG-VPN, que vai ser substituído pela conta certificada.
 */
export type TipoVinculo = 'escopo' | 'interno' | 'transicao'

/** Derivado de `vence_em`/`revogado_em`, nunca gravado — não existe coluna de estado. */
export type EstadoCertificado = 'vigente' | 'vencido' | 'revogado'

export const FRAMEWORK_LABELS: Record<Framework, string> = {
  pci_dss: 'PCI-DSS',
  iso_27001: 'ISO 27001',
}

export interface AmbienteVinculado {
  tenant_id: number
  name: string
  environment: string | null
  tipo: TipoVinculo
  revisar_em: string
  /** Só em `transicao`. `null` = a virada não tem data decidida, e isso é resposta. */
  data_virada: string | null
  motivo: string
  atestado_por: number | null
  atestado_em: string | null
}

export interface CertificadoResumo {
  id: number
  framework: Framework
  versao_framework: string | null
  emissor: string
  documento_numero: string | null
  escopo_texto: string
  emitido_em: string
  vence_em: string
  documento_kb_id: number | null
  revogado_em: string | null
  registrado_em: string | null
  estado: EstadoCertificado
  ambientes: { escopo: number; interno: number; transicao: number }
}

export interface CertificadoDetalhe extends Omit<CertificadoResumo, 'ambientes'> {
  revogado_por: number | null
  registrado_por: number | null
  /** Título do documento na KB, quando há um vinculado. `documento_kb_id: 42` não diz nada a
   *  quem olha a tela, então o detalhe traz o título junto (LEFT JOIN no backend). */
  documento_kb_titulo: string | null
  ambientes: AmbienteVinculado[]
}

/** O que a plataforma afirma sobre UM ambiente. `destino` só vem em `transicao`. */
export interface Selo {
  certificado_id: number | null
  tipo: TipoVinculo
  framework: Framework
  versao_framework: string | null
  emitido_em: string | null
  vence_em: string
  /** Quando olhar este vínculo de novo. **Não afirma nada sobre virada** — foi essa confusão
   *  que fez a tela afirmar viradas que ninguém decidiu, até 29/09/2026 (spec §12.2). */
  revisar_em: string
  /** Só em `transicao`: quando o ambiente deixa de existir. `null` é a resposta honesta. */
  data_virada: string | null
  motivo: string
  destino: string | null
  estado: EstadoCertificado
}

export interface NovoCertificado {
  framework: Framework
  versao_framework?: string | null
  emissor: string
  documento_numero?: string | null
  escopo_texto: string
  emitido_em: string
  vence_em: string
  documento_kb_id?: number | null
}

export interface NovoVinculo {
  tenant_id: number
  tipo: TipoVinculo
  revisar_em: string
  motivo: string
  /** Opcional e só em `transicao`. Omitir é o certo enquanto a data não foi decidida: o backend
   *  recusa com 422 em `escopo` e `interno`, que não têm virada. */
  data_virada?: string | null
}

// O `api` já tem `baseURL: '/api'`, então o prefixo aqui começa em `/v1` — mesma forma do
// `ccm.ts`. Escrever `/api/v1/...` aqui geraria `/api/api/v1/...` e 404 em toda chamada.
const BASE = '/v1/certificacoes'

export const certificacoesApi = {
  list: () => api.get<{ items: CertificadoResumo[] }>(BASE),
  detalhe: (id: number) => api.get<CertificadoDetalhe>(`${BASE}/${id}`),
  /** Chave do mapa é o `tenant_id` em string: JSON não tem chave inteira. */
  selos: () => api.get<{ selos: Record<string, Selo> }>(`${BASE}/selos`),
  registrar: (corpo: NovoCertificado) => api.post<CertificadoDetalhe>(BASE, corpo),
  revogar: (id: number) =>
    api.post<{ id: number; tenants_reavaliados: number[] }>(`${BASE}/${id}/revogar`),
  vincular: (id: number, corpo: NovoVinculo) =>
    api.post<{ tenant_id: number; tipo: TipoVinculo; has_pci: boolean }>(
      `${BASE}/${id}/ambientes`, corpo),
  desvincular: (id: number, tenantId: number) =>
    api.delete<{ tenant_id: number; has_pci: boolean }>(
      `${BASE}/${id}/ambientes/${tenantId}`),
  /** Aponta o certificado para um documento que JÁ está na KB. `null` desvincula.
   *  NÃO sobe arquivo: documento entra na KB pela KB. */
  vincularDocumento: (id: number, documentoKbId: number | null) =>
    api.patch<CertificadoDetalhe>(`${BASE}/${id}/documento`,
                                  { documento_kb_id: documentoKbId }),
}
